'use client'

import { useRouter } from 'next/navigation'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import BootstrapResource from '@/features/veluvit/components/providers/bootstrap-resource'
import ErrorToast from '@/core/components/error-toast'
import { Button } from '@/core/components/form/button/button'
import {
  AppReadScopeContext,
  AppRefreshContext,
} from '@/features/veluvit/hooks/use-app-read-scope'
import { sessionAccessKey } from '@/features/veluvit/lib/app-read'
import { getMutationGeneration } from '@/features/veluvit/services/api'
import { refreshSession } from '@/features/veluvit/services/session.service'
import {
  SessionSnapshotContext,
  useSessionStore,
} from '@/features/veluvit/stores/session.store'

import type { FetcherResponse } from '@/core/lib/fetcher/core'
import type { Bootstrap } from '@/features/veluvit/types/api.types'
import type { ReactNode } from 'react'

type Props = {
  initial: FetcherResponse<Bootstrap>
  epoch: string
  children: ReactNode
}

export default function SessionSnapshotProvider({
  initial,
  epoch,
  children,
}: Props) {
  const router = useRouter()
  const identity = useSessionStore((state) => state.identity)
  const ready = useSessionStore((state) => state.ready)
  const access = useSessionStore((state) => sessionAccessKey(state.data))
  const needsRestoration = useSessionStore(
    (state) => state.data?.sessionRestorationRequired === true
  )
  const initialized = useRef<string | null>(null)
  const [reconciling, setReconciling] = useState<'idle' | 'loading' | 'error'>(
    'idle'
  )
  const refreshRequest = useRef<{
    identity: number
    generation: number
    owner: string | null
    access: string
  } | null>(null)
  const reconciliation = useRef<{
    epoch: string
    promise: Promise<Bootstrap>
  } | null>(null)
  const scope = useMemo(() => {
    const current = useSessionStore.getState()
    const identity = current.ready
      ? current.identity
      : current.identity + (initial.ok && initial.data.user ? 1 : 0)
    return {
      epoch,
      identity,
      access: sessionAccessKey(initial.ok ? initial.data : null),
    }
  }, [epoch, initial])
  const snapshot = useMemo(
    () => ({
      ...useSessionStore.getInitialState(),
      identity: scope.identity,
      ready: initial.ok,
      data: initial.ok ? initial.data : null,
      error: initial.ok ? null : '앱 정보를 불러오지 못했어요.',
    }),
    [initial, scope.identity]
  )
  const lastIdentity = useRef(scope.identity)
  const lastAccess = useRef(scope.access)
  const wasRestoring = useRef(
    initial.ok && initial.data.sessionRestorationRequired === true
  )
  const requestRefresh = useCallback(() => {
    const current = useSessionStore.getState()
    const next = {
      identity: current.identity,
      generation: getMutationGeneration(),
      owner: current.data?.user?.id ?? null,
      access: sessionAccessKey(current.data),
    }
    const pending = refreshRequest.current
    if (
      pending &&
      pending.identity === next.identity &&
      pending.generation === next.generation &&
      pending.owner === next.owner &&
      pending.access === next.access
    )
      return
    refreshRequest.current = next
    router.refresh()
  }, [router])

  useLayoutEffect(() => {
    if (initialized.current === epoch) return
    initialized.current = epoch
    const current = useSessionStore.getState()
    if (!current.ready) {
      if (initial.ok) current.applyBootstrap(initial.data)
      else current.setError('앱 정보를 불러오지 못했어요.')
      return
    }
    const expected = refreshRequest.current
    if (
      initial.ok &&
      expected &&
      expected.identity === current.identity &&
      expected.generation === getMutationGeneration() &&
      expected.owner === (initial.data.user?.id ?? null) &&
      expected.access === sessionAccessKey(initial.data)
    ) {
      current.applyBootstrap(initial.data)
      refreshRequest.current = null
      setReconciling('idle')
      return
    }
    let alive = true
    setReconciling('loading')
    const promise =
      reconciliation.current?.epoch === epoch
        ? reconciliation.current.promise
        : refreshSession()
    reconciliation.current = { epoch, promise }
    void promise
      .then(() => {
        if (alive) {
          if (refreshRequest.current === expected) refreshRequest.current = null
          setReconciling('idle')
        }
      })
      .catch(() => {
        if (alive) setReconciling('error')
      })
    return () => {
      alive = false
      if (initialized.current === epoch) initialized.current = null
    }
  }, [epoch, initial])

  useEffect(() => {
    if (!ready) return
    const restored = wasRestoring.current && !needsRestoration
    wasRestoring.current = needsRestoration
    if (
      lastIdentity.current === identity &&
      lastAccess.current === access &&
      !restored
    )
      return
    lastIdentity.current = identity
    lastAccess.current = access
    requestRefresh()
  }, [identity, access, ready, needsRestoration, requestRefresh])

  return (
    <SessionSnapshotContext.Provider value={snapshot}>
      <AppRefreshContext.Provider value={requestRefresh}>
        <AppReadScopeContext.Provider value={scope}>
          <BootstrapResource />
          {reconciling === 'idle' ? (
            children
          ) : (
            <div className="flex min-h-dvh items-center justify-center gap-3 text-c14 text-description">
              {reconciling === 'error' ? (
                <>
                  <ErrorToast message="계정 정보를 불러오지 못했어요." />
                  <Button variant="secondary" onClick={() => router.refresh()}>
                    다시 시도
                  </Button>
                </>
              ) : (
                <span role="status">계정 정보 확인 중</span>
              )}
            </div>
          )}
        </AppReadScopeContext.Provider>
      </AppRefreshContext.Provider>
    </SessionSnapshotContext.Provider>
  )
}
